/**
 * ManualQuestionPicker — cross-scope picker used by NewSessionDialog when
 * the teacher wants to hand-pick every question instead of relying on
 * per-topic quotas. Fetches ALL visible questions (own + shared, every
 * scope) and lets the user filter by scope/topic/type/search and toggle
 * checkboxes. Emits the selected ID list up to the parent.
 */
import { useEffect, useMemo, useState } from "react";
import { teacherApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, X, ChevronDown, ChevronRight } from "lucide-react";

const BRUT_INPUT = "brut-input border-2 border-[#0F172A] rounded-md h-10 text-sm";

export default function ManualQuestionPicker({
  scopes = [], selectedIds, onChange, max = 50,
  // A/B groups mode: `lockedIds` are IDs already picked by the OTHER group
  // — shown greyed-out with a lock chip, unclickable. `groupLabel` (A/B)
  // and `groupAccent` colour the picked-row background so the teacher can
  // instantly tell which tab they're on.
  lockedIds = null, groupLabel = null, groupAccent = null,
}) {
  const [all, setAll] = useState(null);
  const [scopeFilter, setScopeFilter] = useState("");   // "" = wszystkie
  const [topicFilter, setTopicFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const selSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const lockedSet = useMemo(() => new Set(lockedIds || []), [lockedIds]);

  useEffect(() => {
    // Fetch across all scopes at once — endpoint returns the full teacher-visible pool.
    teacherApi.get("/questions")
      .then(({ data }) => setAll(data))
      .catch(() => toast.error("Nie udało się pobrać puli pytań"));
  }, []);

  const filtered = useMemo(() => {
    if (!all) return [];
    const q = search.trim().toLowerCase();
    return all.filter((qn) => {
      if (scopeFilter && qn.scope !== scopeFilter) return false;
      if (topicFilter && qn.topic !== topicFilter) return false;
      if (typeFilter && qn.type !== typeFilter) return false;
      if (q && !(qn.text || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [all, scopeFilter, topicFilter, typeFilter, search]);

  const topicsInView = useMemo(() => {
    if (!all) return [];
    const src = scopeFilter ? all.filter((qn) => qn.scope === scopeFilter) : all;
    return [...new Set(src.map((qn) => qn.topic).filter(Boolean))].sort();
  }, [all, scopeFilter]);

  const toggle = (id) => {
    if (lockedSet.has(id)) {
      toast.warning(`To pytanie jest już w innej grupie — odznacz je tam najpierw.`);
      return;
    }
    if (selSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      if (selectedIds.length >= max) {
        toast.warning(`Osiągnięto limit ${max} pytań w kartkówce`);
        return;
      }
      onChange([...selectedIds, id]);
    }
  };

  const clearAll = () => onChange([]);

  const scopeLabel = (sc) => scopes.find((s) => s.scope === sc)?.label || sc;

  if (!all) return <div className="text-sm text-[#64748B]">Ładuję pulę…</div>;

  return (
    <div data-testid="manual-picker">
      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
        <div className="relative sm:col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#64748B]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Szukaj w treści pytania…"
            className={`${BRUT_INPUT} pl-9`}
            data-testid="manual-search"
          />
        </div>
        <select
          value={scopeFilter}
          onChange={(e) => { setScopeFilter(e.target.value); setTopicFilter(""); }}
          className={BRUT_INPUT + " px-2"}
          data-testid="manual-scope-filter"
        >
          <option value="">Wszystkie zakresy</option>
          {scopes.filter((s) => s.question_count > 0).map((s) => (
            <option key={s.scope} value={s.scope}>{s.label}</option>
          ))}
        </select>
        <select
          value={topicFilter}
          onChange={(e) => setTopicFilter(e.target.value)}
          className={BRUT_INPUT + " px-2"}
          data-testid="manual-topic-filter"
        >
          <option value="">Wszystkie tematy</option>
          {topicsInView.map((t) => (<option key={t} value={t}>{t}</option>))}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1">
          {["", "open", "single", "multi"].map((t) => (
            <button
              key={t || "all"}
              onClick={() => setTypeFilter(t)}
              className={`brut rounded-md px-2 py-1 text-xs font-bold ${
                typeFilter === t ? "bg-[#0F172A] text-white" : "bg-white"
              }`}
              data-testid={`manual-type-${t || "all"}`}
            >
              {t === "" ? "Wszystkie typy" : t === "open" ? "Otwarte"
                : t === "single" ? "Jeden wybór" : "Wielokrotne"}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span
            className="brut font-black px-2 py-1 rounded-md"
            style={{ backgroundColor: groupAccent || "#FEF08A" }}
            data-testid="manual-count"
          >
            {groupLabel ? `Grupa ${groupLabel}: ` : "Wybrano: "}{selectedIds.length} / {max}
          </span>
          {selectedIds.length > 0 && (
            <button
              onClick={clearAll}
              className="brut bg-white rounded-md px-2 py-1 text-xs font-bold"
              data-testid="manual-clear"
            >
              <X className="w-3 h-3 inline" strokeWidth={2.5} /> Wyczyść
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="border-2 border-[#0F172A] rounded-md max-h-72 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-4 text-sm text-[#64748B]">Brak pytań pasujących do filtrów.</div>
        ) : (
          filtered.slice(0, 300).map((qn) => {
            const picked = selSet.has(qn.id);
            const locked = lockedSet.has(qn.id);
            const isOpen = expandedId === qn.id;
            const rowBg = picked
              ? (groupAccent || "#FEF08A")
              : locked
              ? "#F1F5F9"
              : undefined;
            return (
              <div
                key={qn.id}
                className={`border-b border-[#E2E8F0] text-sm ${
                  picked ? "" : locked ? "opacity-60" : "hover:bg-[#FFFBEB]"
                }`}
                style={rowBg ? { backgroundColor: rowBg } : undefined}
                data-testid={`manual-row-${qn.id}`}
              >
                <div className="flex items-start gap-2 p-2">
                  <input
                    id={`mq-${qn.id}`}
                    type="checkbox"
                    checked={picked}
                    disabled={locked}
                    onChange={() => toggle(qn.id)}
                    className="mt-0.5 w-4 h-4 accent-[#0F172A] flex-shrink-0 cursor-pointer disabled:cursor-not-allowed"
                    data-testid={`manual-toggle-${qn.id}`}
                  />
                  <label
                    htmlFor={`mq-${qn.id}`}
                    className={`flex-1 min-w-0 ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <div className="line-clamp-2 leading-snug">
                      {qn.text}
                      {locked && (
                        <span
                          className="ml-2 brut bg-white border-2 border-[#0F172A] text-[10px] font-black uppercase px-1.5 py-0.5 rounded"
                          data-testid={`manual-locked-${qn.id}`}
                          title="To pytanie jest już przypisane do drugiej grupy"
                        >
                          🔒 w innej grupie
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#64748B] mt-1 flex flex-wrap gap-x-2">
                      <span>📚 {scopeLabel(qn.scope)}</span>
                      <span>🏷️ {qn.topic}</span>
                      <span>
                        {qn.type === "mc" ? "ABCD" : "otwarte"}
                      </span>
                      {qn.owner_type === "shared"
                        ? <span className="text-[#065F46]">✓ wspólne</span>
                        : <span>✎ moje</span>}
                    </div>
                  </label>
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : qn.id)}
                    className="brut bg-white rounded-md p-1 flex-shrink-0 text-[#0F172A]"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Zwiń podgląd" : "Rozwiń podgląd"}
                    title={isOpen ? "Zwiń podgląd" : "Pokaż pełną treść i odpowiedź"}
                    data-testid={`manual-preview-${qn.id}`}
                  >
                    {isOpen
                      ? <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
                      : <ChevronRight className="w-4 h-4" strokeWidth={2.5} />}
                  </button>
                </div>
                {isOpen && (
                  <div
                    className="border-t-2 border-dashed border-[#0F172A] bg-white/70 px-3 py-3 text-[13px] space-y-2"
                    data-testid={`manual-preview-body-${qn.id}`}
                  >
                    <div className="whitespace-pre-wrap leading-snug"><b>Treść:</b> {qn.text}</div>
                    {qn.image_url && (
                      <img
                        src={qn.image_url}
                        alt=""
                        className="brut rounded-md max-h-40 object-contain bg-white"
                      />
                    )}
                    {qn.type === "mc" && Array.isArray(qn.options) && (
                      <ul className="space-y-1">
                        {qn.options.map((opt, i) => {
                          const letter = String.fromCharCode(65 + i);
                          const isCorrect = qn.correct === letter;
                          return (
                            <li
                              key={letter}
                              className={`flex gap-2 items-start ${
                                isCorrect ? "font-bold text-[#065F46]" : ""
                              }`}
                            >
                              <span className="brut rounded-md px-1.5 py-0.5 text-xs font-black bg-white">
                                {letter}
                              </span>
                              <span className="flex-1">{opt}</span>
                              {isCorrect && <span aria-label="poprawne">✓</span>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {qn.type !== "mc" && (
                      <div className="brut bg-[#D1FAE5] rounded-md px-2 py-1 inline-block">
                        <b>Odpowiedź:</b> {qn.correct}
                      </div>
                    )}
                    {qn.explanation && (
                      <div className="text-[#334155]">
                        <b>Wyjaśnienie:</b> {qn.explanation}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        {filtered.length > 300 && (
          <div className="p-2 text-xs text-[#64748B] italic bg-[#F8FAFC]">
            Pokazano pierwsze 300 wyników — zawęź filtry, żeby zobaczyć pozostałe.
          </div>
        )}
      </div>
    </div>
  );
}
